/**
 * 简体中文 — Simplified Chinese strings.
 *
 * THE KEY CONVENTION LIVES IN `en.ts`. Read it there before adding anything;
 * this file only holds the other half of every pair.
 *
 * Two things this file enforces on its own:
 *
 *  - COMPLETENESS. The `Record<LocaleKey, string>` annotation below means a key
 *    that exists in `en.ts` and not here fails `npm run build`, and a key that
 *    exists only here fails too. There is no silent English fallback to hide
 *    behind, which is the point: twelve agents are translating in parallel.
 *
 *  - ORDER. Keep the sections and the keys inside them in the same order as
 *    `en.ts`, so the two files diff side by side.
 *
 * On the vocabulary: `domain.*` is the spine of the product and is fixed —
 * 接口 / 系列 / 健康状态 / 正常 / 降级 / 故障 / 未知 / 请求数 / 错误率 /
 * 状态码 / 时间范围 / 抽样估算 / 备用验证码 / 双重验证 / 邀请 /
 * 超级管理员 / 成员 / 流量骤降 / 无数据. Reuse the key; do not re-translate
 * the word in your own namespace.
 *
 * And the distinction that must survive translation: 无数据 (`domain.noData`,
 * we saw nothing) is NOT 0 次请求 (`common.requestCount` with count 0, we saw
 * the endpoint and it was silent). This monitor never probes anything, so
 * collapsing the two would report an outage every time an endpoint idles.
 */
import type { LocaleKey } from './en'

export const zh: Record<LocaleKey, string> = {
  // ── common ───────────────────────────────────────────────────────────────
  'common.cancel': '取消',
  'common.close': '关闭',
  'common.confirm': '确认',
  'common.continue': '继续',
  'common.copy': '复制',
  'common.copied': '已复制',
  'common.error': '发生错误。',
  'common.loading': '加载中…',
  'common.retry': '重试',
  'common.save': '保存',
  'common.saving': '保存中…',
  // 中文没有单复数变化，_one 与 _other 相同 —— 但两个键都必须存在。
  'common.requestCount_one': '{{count}} 次请求',
  'common.requestCount_other': '{{count}} 次请求',
  'common.endpointCount_one': '{{count}} 个接口',
  'common.endpointCount_other': '{{count}} 个接口',

  // ── domain ───────────────────────────────────────────────────────────────
  'domain.endpoint': '接口',
  'domain.family': '系列',
  'domain.health': '健康状态',
  'domain.health.healthy': '正常',
  'domain.health.degraded': '降级',
  'domain.health.down': '故障',
  'domain.health.unknown': '未知',
  'domain.requests': '请求数',
  'domain.errorRate': '错误率',
  'domain.statusCode': '状态码',
  'domain.window': '时间范围',
  'domain.sampled': '抽样估算',
  'domain.estimated': '估算值',
  // 与 count 为 0 的 common.requestCount（0 次请求）严格区分。
  'domain.noData': '无数据',
  'domain.trafficCliff': '流量骤降',
  'domain.backupCodes': '备用验证码',
  'domain.twoFactor': '双重验证',
  'domain.twoFactorShort': '双重验证',
  'domain.invite': '邀请',
  'domain.role.superadmin': '超级管理员',
  'domain.role.member': '成员',

  // ── nav (src/nav) ────────────────────────────────────────────────────────
  'nav.header.title': 'API 健康状态',
  'nav.header.mainNav': '主导航',
  'nav.header.dashboard': '仪表盘',
  'nav.header.profile': '个人资料',
  'nav.header.admin': '管理',
  'nav.header.signOut': '退出登录',
  'nav.language.label': '语言',
  'nav.document.title': 'API 接口健康监控',

  // ── auth (src/auth) ──────────────────────────────────────────────────────

  'auth.login.title': '登录',
  'auth.login.subtitle': 'TikHub API 健康状态监控 —— 仅限内部访问。',
  'auth.login.email': '邮箱',
  'auth.login.password': '密码',
  'auth.login.submit': '登录',
  'auth.login.missingMfaToken': '服务器未签发双重验证令牌，请重试。',
  'auth.login.missingEnrollmentToken': '此账号必须设置双重验证，但服务器未签发绑定令牌，请重试。',

  'auth.twoFactor.subtitle': '请输入验证器应用中的 6 位验证码。',
  'auth.twoFactor.codeLabel': '验证码',
  'auth.twoFactor.codeHint': '手机丢失或不在身边？可以使用 10 位备用验证码，每个仅可使用一次。',
  'auth.twoFactor.submit': '验证',
  'auth.twoFactor.useDifferentAccount': '使用其他账号登录',
  'auth.twoFactor.codeTooShort': '请输入验证器应用中的 {{length}} 位验证码。',
  'auth.twoFactor.expired': '本次登录已超时，请重新输入密码。',

  'auth.requireAuth.checkingSession': '正在检查登录状态',

  // 安全文案：邮箱错误与密码错误共用同一条提示，且不得细分 —— 后端为不存在的
  // 账号也会计算一次哈希以对齐耗时，措辞一旦放宽就会把它挡住的账号枚举还回去。
  'auth.error.badCredentials': '邮箱或密码不正确。',
  'auth.error.badCode': '验证码无效。请使用验证器中的下一个验证码，或使用备用验证码。',
  'auth.error.lockedOut': '失败尝试次数过多，此账号已锁定 15 分钟。',
  'auth.error.invalidInput': '请检查填写的信息。',
  'auth.error.failed': '登录失败（{{status}}），请重试。',
  'auth.error.unreachable': '无法连接服务器。请检查网络后重试。',

  // ── account (src/account) ────────────────────────────────────────────────

  // ConfirmAction
  'account.confirm.typePhrase': '请输入 {{phrase}} 以确认',

  // AdminPage
  'account.admin.denied': '团队管理仅限超级管理员使用。如需变更账号，请联系超级管理员。',
  'account.admin.title': '团队',
  'account.admin.lede':
    '账号、三项可恢复的操作，以及一项无法撤销的操作。此处的每一项操作都会在服务端再次校验。',
  'account.admin.membersTitle': '团队成员',
  'account.admin.accountCount_one': '{{count}} 个账号',
  'account.admin.accountCount_other': '{{count}} 个账号',
  'account.admin.dismiss': '关闭提示',
  'account.admin.loading': '正在加载团队成员…',

  'account.team.col.email': '邮箱',
  'account.team.col.role': '角色',
  'account.team.col.active': '启用状态',
  'account.team.col.mustChangePassword': '需修改密码',
  'account.team.col.actions': '操作',

  // 「已关闭（账号要求启用）」与「已关闭」是两种账号状态：前者在启用前无法使用产品。
  'account.team.flag.yes': '是',
  'account.team.flag.no': '否',
  'account.team.flag.on': '已开启',
  'account.team.flag.off': '已关闭',
  'account.team.flag.offRequired': '已关闭（账号要求启用）',
  'account.team.flag.aria': '{{label}}：{{state}}',

  'account.team.you': '（你）',
  'account.team.selfTitle': '不能对自己的账号执行此操作。',
  'account.team.selfNote':
    '这是你自己的账号：请在个人资料页修改密码或双重验证。服务端会拒绝对发起请求的账号执行这三项操作。',

  'account.team.resetPassword.label': '重置密码',
  'account.team.resetPassword.question': '确定重置 {{email}} 的密码吗？',
  'account.team.resetPassword.detail':
    '系统会生成一个随机临时密码，且仅显示一次。对方当前的密码立即失效，所有已登录会话全部结束，' +
    '并且必须在下次登录时设置新密码。',
  'account.team.resetPassword.confirm': '确认重置密码',

  'account.team.disable2fa.label': '关闭双重验证',
  'account.team.disable2fa.question': '确定关闭 {{email}} 的双重验证吗？',
  'account.team.disable2fa.detail':
    '适用于验证器丢失的情况。TOTP 密钥和所有未使用的备用验证码都会被销毁，对方的所有会话全部结束。' +
    '如果该账号要求启用双重验证，对方必须在下次登录时重新设置；否则在重新启用之前，该账号仅凭密码登录。',
  'account.team.disable2fa.confirm': '确认关闭双重验证',
  'account.team.disable2fa.inapplicable': '该账号未启用双重验证。',
  'account.team.disable2fa.notice':
    '{{email}} 的双重验证已关闭。对方已被退出登录，并将在下次登录时被要求重新设置双重验证。',

  'account.team.deactivate.label': '停用',
  'account.team.deactivate.question': '确定停用 {{email}} 吗？',
  'account.team.deactivate.detail':
    '对方会立即在所有设备上退出登录，并且无法再次登录。重新启用需要在服务器上执行命令。',
  'account.team.deactivate.confirm': '确认停用',
  'account.team.deactivate.inapplicable': '该账号已处于停用状态。',
  'account.team.deactivate.notice': '{{email}} 已停用并退出登录（active: {{active}}）。',

  'account.team.delete.label': '删除账号',
  'account.team.delete.question': '确定永久删除 {{email}} 吗？',
  'account.team.delete.detail':
    '此操作无法撤销。该账号、它的所有会话及未使用的备用验证码都会被销毁，' +
    '它发出的、尚未被使用的邀请也会随之失效。它在审计日志中的记录会被保留 —— ' +
    '与账号解除关联，并标注为该邮箱地址 —— 因此它做过什么的记录会在账号消失后继续留存。' +
    '如果只是想让对方退出登录，请改用「停用」。',
  'account.team.delete.confirm': '删除该账号',

  // 英文按各自的数量变形，所以分成两个子句；整句在每种语言里都是一句完整的话。
  'account.team.deleted.invites_one': '已撤销 {{count}} 个未使用的邀请',
  'account.team.deleted.invites_other': '已撤销 {{count}} 个未使用的邀请',
  'account.team.deleted.audit_one':
    '保留了 {{count}} 条审计日志（标注为该邮箱地址，且不再关联任何账号）',
  'account.team.deleted.audit_other':
    '保留了 {{count}} 条审计日志（标注为该邮箱地址，且不再关联任何账号）',
  // 分号分隔两个子句：两句里都有逗号，再用逗号连接会分不清层次。
  'account.team.deleted.summary': '{{email}} 已被永久删除。{{invites}}；{{audit}}。',

  'account.team.tempPassword.title': '{{email}} 的临时密码',
  'account.team.tempPassword.warning':
    '仅显示一次。系统不会以可读形式保存它，因此无法再次查看 —— 请立即复制，' +
    '并当面或通过你信任的渠道交给对方。对方必须在下次登录时修改密码。',
  'account.team.tempPassword.label': '临时密码',

  // InviteMemberForm
  'account.invite.title': '添加团队成员',
  'account.invite.lede': '本服务没有自助注册，邀请是账号产生的唯一途径。',
  'account.invite.email': '邮箱',
  'account.invite.role': '角色',
  'account.invite.submit': '发送邀请',
  'account.invite.reveal.title': '给 {{email}} 的邀请',
  'account.invite.reveal.warning':
    '仅显示一次。该令牌是对方唯一的注册途径 —— 请立即复制，并通过你信任的渠道发送。' +
    '关闭本面板即会丢失，唯一的补救办法是重新发一份邀请。',
  'account.invite.linkLabel': '邀请链接',
  // {{role}} 是产品术语（成员 / 超级管理员），与角色下拉框和团队表格一致；
  // 只有本版本不认识的角色才按服务端返回的 id 原样显示。
  'account.invite.linkHint': '有效期至 {{expires}} · 角色：{{role}}',
  'account.invite.tokenLabel': '邀请令牌',
  'account.invite.tokenHint': '供不想点链接、更愿意直接粘贴令牌的人使用。',

  // 管理操作的请求失败。服务端自己的那句话优先，这些只是没有那句话时的兜底。
  'account.adminError.forbidden': '只有超级管理员可以执行此操作。',
  'account.adminError.invalid': '请检查填写的信息。',
  'account.adminError.server': '服务器无法完成该操作（{{status}}）。请重试。',
  'account.adminError.failed': '请求失败（{{status}}）。',
  'account.adminError.network': '无法连接服务器。请检查网络后重试。',

  // InviteAcceptPage
  'account.inviteAccept.title': '接受邀请',
  'account.inviteAccept.subtitle':
    'TikHub API 健康状态监控 —— 此链接是创建账号的唯一途径，双重验证也会作为创建过程的一部分一并设置。',
  'account.inviteAccept.tokenLabel': '邀请令牌',
  'account.inviteAccept.tokenHint': '已根据邀请链接自动填入。如果你只拿到了令牌，请手动粘贴。',
  'account.inviteAccept.passwordLabel': '设置密码',
  'account.inviteAccept.passwordHint': '至少 {{min}} 个字符。',
  'account.inviteAccept.confirmLabel': '确认密码',
  'account.inviteAccept.submit': '创建账号',
  'account.inviteAccept.already': '已经创建过账号？',
  'account.inviteAccept.signIn': '登录',
  'account.inviteAccept.created.title': '账号已创建',
  'account.inviteAccept.created.fallbackSubject': '你的账号',
  'account.inviteAccept.created.body':
    '{{subject}} 已就绪，但该账号仍要求启用双重验证，而本页面没有收到设置链接。',
  'account.inviteAccept.created.next': '请用你刚刚设置的密码登录，以完成双重验证的设置。',
  'account.inviteAccept.error.noToken': '请粘贴你收到的邀请令牌 —— 邀请链接本应已自动填入。',
  'account.inviteAccept.error.short': '密码至少需要 {{min}} 个字符。',
  'account.inviteAccept.error.mismatch': '两次输入的密码不一致。',
  'account.inviteAccept.error.invalidFallback': '该邀请无法使用。',
  'account.inviteAccept.error.invalid':
    '{{detail}} 如果你已经用此链接创建过账号，请直接登录；否则请向邀请你的人索取一份新的邀请。',
  'account.inviteAccept.error.exists': '该邮箱的账号已存在。请直接登录，或请管理员重置密码。',
  'account.inviteAccept.error.server': '无法创建账号（{{status}}）。请重试。',
  'account.inviteAccept.error.network': '无法连接服务器。请检查网络后重试。',

  // TwoFactorSetup。标题直接复用 domain.twoFactor。
  'account.twoFactorSetup.requirementRequired':
    '本账号强制启用：每次登录时，除密码外还需输入验证器应用生成的验证码。' +
    '该设置无法关闭，也无法稍后再完成。',
  'account.twoFactorSetup.requirement':
    '每次登录时，除密码外还需输入验证器应用生成的验证码。',
  'account.twoFactorSetup.signInAgain': '重新登录',
  'account.twoFactorSetup.enabledNow': '你的账号已启用双重验证。',
  'account.twoFactorSetup.acknowledge': '我已保存备用验证码，继续',
  'account.twoFactorSetup.start': '设置双重验证',
  'account.twoFactorSetup.step1': '1. 用验证器应用扫描此二维码。',
  'account.twoFactorSetup.qrAlt': '双重验证绑定二维码',
  'account.twoFactorSetup.step2': '2. 或手动输入此密钥。',
  'account.twoFactorSetup.setupKey': '设置密钥',
  'account.twoFactorSetup.step3': '3. 输入应用当前显示的验证码。',
  'account.twoFactorSetup.codeLabel': '验证码',
  'account.twoFactorSetup.submit': '确认并启用',
  'account.twoFactorSetup.enabledTitle': '已启用',
  'account.twoFactorSetup.enabledHelp':
    '每次登录时，账号都会要求输入验证器应用的验证码。手机丢失时，' +
    '可用备用验证码登录，或请超级管理员重置该账号的双重验证。',
  'account.twoFactorSetup.error.codeLength': '请输入验证器应用显示的 {{length}} 位验证码。',
  'account.twoFactorSetup.error.expiredLink':
    '该绑定链接已失效。请重新登录，以完成双重验证的设置。',
  'account.twoFactorSetup.error.alreadyEnabledReload': '该账号已启用双重验证。请刷新页面。',
  'account.twoFactorSetup.error.mustChangePassword':
    '请先修改密码——在此之前，账号的其余功能均处于锁定状态。',
  'account.twoFactorSetup.error.sessionExpired': '登录状态已过期，请重新登录。',
  'account.twoFactorSetup.error.enrollFailed': '无法开始绑定（{{status}}），请重试。',
  'account.twoFactorSetup.error.network': '无法连接服务器。请检查网络后重试。',
  'account.twoFactorSetup.error.codeRejectedToken':
    '验证码未通过。请确认读取的是本页二维码对应的验证码——之前那次尝试添加的条目已经失效，' +
    '永远不会再生成有效验证码——然后核对手机时间，并用下一个验证码重试。',
  'account.twoFactorSetup.error.codeRejected': '验证码未通过。请核对手机时间，并用下一个验证码重试。',
  'account.twoFactorSetup.error.alreadyEnabled': '该账号已启用双重验证。',
  'account.twoFactorSetup.error.confirmFailed': '无法启用双重验证（{{status}}），请重试。',

  // BackupCodes。全应用中后果最严重的一句话：没有任何补发接口，
  // 用户看漏了就会被永久锁在账号外面。中文必须同样斩钉截铁。
  'account.backupCodes.warning':
    '请立即保存这些备用验证码。这是它们唯一一次显示——服务器只保存哈希值，' +
    '此后无法再次查看，也无法找回。每个验证码只能使用一次，可代替验证器应用生成的验证码。',
  'account.backupCodes.copy': '复制验证码',
  'account.backupCodes.download': '下载验证码',
  'account.backupCodes.copied': '已复制到剪贴板。',
  'account.backupCodes.copyFailed': '无法访问剪贴板——请选中上方的验证码并手动复制。',

  // SecretReveal。title 与 warning 是 props，由调用方翻译。
  'account.secretReveal.dismiss': '关闭',
  'account.secretReveal.copyValue': '复制{{label}}',
  'account.secretReveal.copyFailed': '复制失败——请选中后手动复制。',

  // EnrollmentStep。整句成键，不用片段拼接。
  'account.enrollment.title': '完成账号设置',
  'account.enrollment.intro':
    '使用监控面板之前必须先启用双重验证——此步骤无法跳过，也无法稍后再来完成。',
  'account.enrollment.introWithEmail':
    '{{email}} 已就绪。使用监控面板之前必须先启用双重验证——此步骤无法跳过，' +
    '也无法稍后再来完成。',

  // ProfilePage —— 强制改密提示条。
  'account.profile.rotationTitle': '请立即修改密码',
  'account.profile.rotationBody':
    '此账号仍在使用他人下发的密码——来自邀请或管理员重置。在你更换密码之前，应用的其余部分都不可用：' +
    '只要该标记还在，服务端就会拒绝其他所有请求，导航链接也会把你带回本页。请在下方设置你自己的密码。',

  // AccountSummary。双重验证一行的标签用 domain.twoFactor，角色用 domain.role.*。
  'account.summary.title': '账号',
  'account.summary.note': '账号仅能通过邀请创建；如需修改邮箱或角色，请联系超级管理员。',
  'account.summary.email': '邮箱',
  'account.summary.role': '角色',
  'account.summary.twoFactorEnabled': '已启用',
  'account.summary.twoFactorDisabled': '未启用',

  // ChangePasswordForm。{{min}} 即 MIN_PASSWORD_LENGTH，始终插值，不写死数字。
  'account.changePassword.title': '修改密码',
  'account.changePassword.note': '修改密码后，其他所有设备上的登录都会被退出；当前浏览器保持登录状态。',
  'account.changePassword.currentLabel': '当前密码',
  'account.changePassword.newLabel': '新密码',
  'account.changePassword.newHint': '至少 {{min}} 个字符。',
  'account.changePassword.confirmLabel': '确认新密码',
  'account.changePassword.submit': '修改密码',
  'account.changePassword.tooShort': '新密码至少需要 {{min}} 个字符。',
  'account.changePassword.mismatch': '两次输入的新密码不一致。',
  'account.changePassword.success': '密码已修改，其他所有会话均已退出登录。',
  'account.changePassword.errorWrongCurrent': '当前密码不正确。',
  'account.changePassword.errorSessionExpired': '登录状态已过期，请重新登录。',
  'account.changePassword.errorStatus': '无法修改密码（{{status}}）。请重试。',
  'account.changePassword.errorNetwork': '无法连接服务器。请检查网络后重试。',

  // ── dashboard (src/dashboard) ────────────────────────────────────────────


  // EndpointTable
  'dashboard.table.aria': '接口列表',
  'dashboard.table.colMix': '状态分布',
  'dashboard.table.colErrors': '错误率',
  'dashboard.table.colTrend': '趋势',
  'dashboard.table.sortHint': '按此列排序；按住 Shift 可追加次级排序。',
  // 中文的语序与英文不同，因此整句一个键，由占位符定位三个数字。
  'dashboard.table.summary': '{{families}} 个系列、共 {{total}} 个接口，当前显示 {{visible}} 个',
  'dashboard.table.searchLabel': '按路径或系列筛选接口',
  'dashboard.table.searchPlaceholder': '按路径或系列筛选…',
  'dashboard.table.expandAll': '全部展开',
  'dashboard.table.collapseAll': '全部收起',
  'dashboard.table.emptyFiltered': '没有接口符合当前筛选条件。',
  'dashboard.table.emptySearch': '没有接口路径或系列匹配“{{query}}”。',
  'dashboard.table.errorTitle': '接口列表不可用 —— {{detail}}',
  'dashboard.table.errorHttp': 'HTTP {{status}}',
  'dashboard.table.errorUnreachable': '无法连接服务器。',

  // EndpointRow。noData 是破折号的提示：没有测到，而不是测到了 0。
  'dashboard.row.noData': '该时间范围内此接口无数据。',
  'dashboard.row.noRequestsInBucket': '该状态码区间内没有请求。',
  'dashboard.row.cliffNotApplicable':
    '流量骤降检测以 24h 基线作为比较对象，因此仅适用于短于 24h 的时间范围。',
  'dashboard.row.cliffNoBaseline': '此接口没有流量基线，无从比较。',
  'dashboard.row.cliffTitle':
    '该时间范围内的流量远低于 24h 基线速率 —— 此接口可能在请求进入 Cloudflare 日志之前就已失败。',
  'dashboard.row.cliffBadge': '流量骤降',
  'dashboard.row.steady': '平稳',

  // FamilyGroup
  'dashboard.family.statusNone': '本时间范围内该系列没有测得 {{bucket}} 数据。',
  'dashboard.family.statusTotal': '该系列共 {{count}} 次 {{bucket}} 响应。',
  'dashboard.family.count': '已显示 {{visible}}/{{total}}',
  'dashboard.family.chipTitle': '{{count}} 个{{state}} —— {{hint}}',
  'dashboard.family.totalNone':
    '该时间范围内此系列没有任何接口返回数据，因此无总计可报。这与 0 次请求不是一回事。',
  'dashboard.family.totalPartial':
    '该时间范围内此系列有 {{measured}}/{{total}} 个接口返回了数据，此处为这些接口的请求数合计；其余接口未被测量。',
  'dashboard.family.totalAll': '该时间范围内此系列全部接口的请求数合计。',

  // StatusRatioBar。无数据（什么都没看到）与 0 次请求（看到了，且没有流量）
  // 是两件事，任何时候都不能合并。
  'dashboard.ratioBar.noData': '无数据',
  'dashboard.ratioBar.noDataTitle':
    'Cloudflare 在该时间范围内没有报告此接口的任何请求。这是证据缺失，而不是零流量的证据。',
  'dashboard.ratioBar.zeroTitle': '该时间范围内此接口没有处理任何请求。',
  'dashboard.ratioBar.noStatusDetail': '无状态码明细',
  'dashboard.ratioBar.segment': '{{bucket}} {{share}}',
  // 列表分隔符，不是句子：中文用顿号。
  'dashboard.ratioBar.separator': '、',
  'dashboard.ratioBar.title': '{{total}} 次请求 —— {{breakdown}}',

  // FreshnessBadge
  'dashboard.freshness.noTimestamp': '时间戳不可用',
  'dashboard.freshness.asOf': '数据截至 {{time}} PT',
  'dashboard.freshness.behind': '落后 {{age}}',
  'dashboard.freshness.lagTitle': 'Cloudflare 分析数据延迟定稿，因此时间范围的结束时间总是在过去。',
  'dashboard.freshness.ageSeconds': '{{n}} 秒',
  'dashboard.freshness.ageMinutes': '{{n}} 分钟',
  'dashboard.freshness.ageHours': '{{n}} 小时',
  'dashboard.freshness.ageUnknown': '未知时长',
  'dashboard.freshness.estimated': '~抽样估算',
  'dashboard.freshness.sampleRate': '大约每 {{n}} 次请求抽样 1 次',
  'dashboard.freshness.sampleRateUnknown': '仅抽取了部分请求',
  'dashboard.freshness.sampleTitle':
    'Cloudflare 对该时间范围做了抽样（{{rate}}）。此处的每个计数都是按样本放大得出的统计估算值，而非精确统计。',
  'dashboard.freshness.stale': '数据陈旧 —— 无法连接 Cloudflare',

  // EndpointDetail
  'dashboard.detail.regionLabel': '接口详情',
  'dashboard.detail.healthWindowHint': '上方的健康状态对应本页当前选定的时间范围。',
  'dashboard.detail.inWindow': '{{window}}内',
  'dashboard.detail.windowCardTitle': '{{window}} —— 在下方饼图中查看该时间范围',
  'dashboard.detail.notCached': '尚未缓存',
  'dashboard.detail.notCachedTitle':
    '该时间范围尚未进入后端缓存，因此未随本次请求返回。调度任务预热该时间范围后即会出现。',
  'dashboard.detail.noDataTitle': '该接口在此时间范围内无数据。',
  'dashboard.detail.requestsLabel': '请求数',
  'dashboard.detail.errorsLabel': '错误率',
  'dashboard.detail.trafficCliff': '流量骤降',
  'dashboard.detail.trafficCliffTitle':
    '该时间范围内的流量远低于 24h 基线速率 —— 请求可能在到达 Cloudflare 日志之前就已失败。',
  'dashboard.detail.unreachable': '无法连接服务器。',
  'dashboard.detail.gone': '该接口已不在注册表中 —— {{detail}}。',
  'dashboard.detail.unavailable': '无法获取接口详情 —— {{detail}}',

  'dashboard.scale.shares': '占比',
  'dashboard.scale.counts': '计数',

  // StatusPie
  'dashboard.pie.regionLabel': '所选时间范围的状态码构成',
  'dashboard.pie.heading': '状态码构成',
  'dashboard.pie.headingWindow': '（{{window}}）',
  'dashboard.pie.scaleGroup': '饼图刻度',
  'dashboard.pie.emptyNotCached': '该时间范围尚未进入后端缓存，暂无可绘制的数据。',
  'dashboard.pie.emptyNoRequests': '该时间范围内未记录到任何请求，没有可绘制的构成。',
  'dashboard.pie.footer_one': '{{total}} 次请求，涵盖 {{codes}} 个状态码',
  'dashboard.pie.footer_other': '{{total}} 次请求，涵盖 {{codes}} 个状态码',

  // StatusTimeSeries —— 「未测量」与「0 次请求」必须保持区分，见 en.ts 第 5 条。
  'dashboard.timeSeries.heading': '各时间范围的状态码构成',
  'dashboard.timeSeries.headingHint': '（左侧为近期，右侧为长期基线）',
  'dashboard.timeSeries.headingTitle':
    '六个时间范围是嵌套关系 —— 7d 包含 24h，24h 包含 30m —— 因此这些柱状图是同一接口的六种视角，而非六个连续时段。',
  'dashboard.timeSeries.scaleGroup': '柱状图刻度',
  'dashboard.timeSeries.empty': '所有已缓存的时间范围内均无请求数据，没有可绘制的构成。',
  'dashboard.timeSeries.notMeasured': '未测量',
  'dashboard.timeSeries.noRequests': '0 次请求',

  // DashboardPage / Overview / StatusFilter / WindowPicker。
  'dashboard.page.detailPanel': '接口详情',

  'dashboard.windowPicker.label': '时间范围',

  'dashboard.statusFilter.noneSelected': '未选中任何健康状态，表格为空。',
  'dashboard.statusFilter.showAll': '显示全部',

  'dashboard.overview.label': '总览',
  'dashboard.overview.requests': '次请求',
  // 「看到了这个时间范围，里面一次请求都没有」，不是 domain.noData（无数据）。
  'dashboard.overview.noRequests': '该时间范围内没有请求。',
  'dashboard.overview.composition': '状态码构成：{{parts}}',
  'dashboard.overview.compositionPart': '{{label}} {{share}}',
  'dashboard.overview.listSeparator': '、',
  'dashboard.overview.bucketTooltip': '{{label}}：{{count}}（{{share}}）',
  'dashboard.overview.tileShare': '占全部 {{total}} 个接口的 {{share}}',
  'dashboard.overview.tileNoEndpoints': '无接口',
  'dashboard.overview.unreachable': '无法连接服务器。',
  'dashboard.overview.error': '总览不可用：{{detail}}',

  'dashboard.healthHint.down': '以服务器错误为主，或该时间范围内的请求全部失败。',
  'dashboard.healthHint.degraded': '错误率高于正常阈值，但仍在提供服务。',
  'dashboard.healthHint.healthy': '错误率在正常范围内。',
  'dashboard.healthHint.unknown': '该时间范围内流量太少，无法判断，不等同于正常。',
  'dashboard.statusBucket.2xx': '成功',
  'dashboard.statusBucket.3xx': '重定向',
  'dashboard.statusBucket.4xx': '客户端错误',
  'dashboard.statusBucket.429': '限流或配额耗尽（429、402）',
  'dashboard.statusBucket.402': '需要付费——调用方额度或余额已用尽。',
  'dashboard.statusColumn.200': '状态码为 200 的响应。',
  'dashboard.statusColumn.400': '状态码为 400 的响应——请求格式有误。',
  'dashboard.statusColumn.401': '状态码为 401 的响应——调用方未通过身份验证。',
  'dashboard.statusColumn.403': '状态码为 403 的响应——调用方已认证但无权访问。',
  'dashboard.statusColumn.404': '状态码为 404 的响应——资源不存在。',
  'dashboard.statusColumn.405': '状态码为 405 的响应——该接口不支持此 HTTP 方法。',
  'dashboard.statusColumn.402': '状态码为 402 的响应——调用方额度或余额已用尽。',
  'dashboard.statusColumn.429': '状态码为 429 的响应——调用方被限流。',
  'dashboard.statusBucket.5xx': '服务器错误',

  // ── api (src/api) ────────────────────────────────────────────────────────

  // 窗口 id（'30m'、'7d'）是 API 取值，不翻译。
  'api.window.30m': '最近 30 分钟',
  'api.window.6h': '最近 6 小时',
  'api.window.12h': '最近 12 小时',
  'api.window.24h': '最近 24 小时',
  'api.window.72h': '最近 72 小时',
  'api.window.7d': '最近 7 天',
}
